import React, { useState, useRef } from 'react';
import MenuIcon from '@material-ui/icons/Menu';

import {
  makeStyles,
  Box,
  AppBar,
  Toolbar,
  IconButton,
  Typography,
  Drawer,
  Divider
} from '@material-ui/core';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import { styled } from '@mui/material/styles';
import { useDispatch, useSelector } from 'react-redux';
import { logout } from 'src/redux/slices/authJwt';
import {
  UserCheck,
  Users,
  LogOut,
  ChevronDown,
  Briefcase,
  ClipboardList
} from 'lucide-react';
import qrata from '../../src/qrataLogo.svg';
import {
  NavLink as RouterLink,
  useLocation,
  useHistory
} from 'react-router-dom';
import { PATH_APP } from 'src/routes/paths';

const MOBILE_DRAWER_ITEMS = [
  { label: 'Jobs', icon: Briefcase, tab: 0 },
  { label: 'My Applications', icon: ClipboardList, tab: 1 }
];

const drawerWidth = 256;

const useStyles = makeStyles((theme) => ({
  root: {},
  desktopBar: {
    '@media (max-width: 959.95px)': {
      display: 'none'
    }
  },
  mobileBar: {
    '@media (min-width: 960px)': {
      display: 'none'
    }
  },
  AppBar: {
    boxShadow: 'none',
    backgroundColor: '#0b2239',
    zIndex: theme.zIndex.drawer + 1
  },
  mobileHeader: {
    backgroundColor: '#0b2239',
    top: 0,
    bottom: 'auto'
  },
  mobileToolbar: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    minHeight: '56px',
    height: '56px'
  },
  toolbarContainer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '0 16px',
    width: '100%'
  },
  menuButton: {
    color: '#FFFFFF',
    width: 40,
    height: 40,
    borderRadius: '8px',
    '&:hover': {
      backgroundColor: 'rgba(255, 255, 255, 0.1)'
    }
  },
  drawerPaper: {
    width: drawerWidth,
    background: '#F8FAFC',
    borderRight: '1px solid #E2E8F0'
  },
  drawerNav: {
    padding: '16px 12px'
  },
  drawerItem: {
    width: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-start',
    gap: 12,
    padding: '10px 14px',
    marginBottom: 6,
    borderRadius: 12,
    border: 'none',
    background: 'transparent',
    cursor: 'pointer',
    textAlign: 'left',
    fontFamily: 'Inter',
    fontSize: 14,
    fontWeight: 600,
    color: '#504949',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    transition: 'background-color 150ms ease, color 150ms ease',
    '&:hover': {
      backgroundColor: 'rgba(226, 232, 240, 0.7)',
      color: '#0F172A'
    }
  },
  drawerItemActive: {
    backgroundColor: '#003853',
    color: '#FFFFFF',
    boxShadow: '0 1px 2px rgba(0, 0, 0, 0.05)',
    '&:hover': {
      backgroundColor: '#003853',
      color: '#FFFFFF'
    }
  },

  listItemText: {
    color: '#003458',
    '&:hover': {
      color: 'rgb(54, 176, 154)'
    }
  },
  listItemIcon: {
    color: '#003458',
    '&:hover': {
      color: 'rgb(54, 176, 154)'
    }
  }
}));

const StyledMenu = styled((props) => (
  <Menu
    elevation={0}
    anchorOrigin={{
      vertical: 'bottom',
      horizontal: 'right'
    }}
    transformOrigin={{
      vertical: 'top',
      horizontal: 'right'
    }}
    {...props}
  />
))(({ theme }) => ({
  '& .MuiPaper-root': {
    borderRadius: 12,
    width: 236,
    minWidth: 236,
    color: '#374151',
    border: '1px solid #e5e7eb',
    boxShadow: '0 16px 40px rgba(11, 34, 57, 0.2)',
    marginTop: 8,
    '& .MuiMenu-list': {
      padding: '6px'
    },
    '& .MuiMenuItem-root': {
      borderRadius: 8,
      padding: '9px 10px',
      fontSize: 13,
      fontFamily: 'Inter',
      gap: 10,
      '&:hover': {
        backgroundColor: '#d9f7f0',
        color: '#14b8a6'
      },
      '&:active': {
        backgroundColor: '#d9f7f0'
      }
    }
  }
}));

function TopBar({
  logoOnly = false,
  position = 'sticky',
  brandTo = PATH_APP.general.opportunities
}) {
  const classes = useStyles();
  const dispatch = useDispatch();
  const history = useHistory();
  const anchorRef = useRef(null);
  const { pathname, search } = useLocation();
  const currentTab = Number(new URLSearchParams(search).get('tab')) || 0;
  const [openMenu, setOpenMenu] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const isHome = pathname === '/';

  const user = useSelector((state) => state.authJwt?.user);
  const userPhoto = useSelector(
    (state) => state.user?.talentCard?.profilePictureURL
  );
  const fullName =
    [user?.firstName, user?.lastName].filter(Boolean).join(' ') ||
    user?.email ||
    'User';
  const initials = fullName
    .split(' ')
    .map((n) => n[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);

  const [anchorEl, setAnchorEl] = useState(null);

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = () => {
    sessionStorage.removeItem('modalClosed');
    dispatch(logout()).then(() => {
      handleClose();
      history.push('/auth/login');
    });
  };

  const toggleDrawer = (open) => (event) => {
    if (
      event.type === 'keydown' &&
      (event.key === 'Tab' || event.key === 'Shift')
    ) {
      return;
    }
    setDrawerOpen(open);
  };

  const menuItemSx = {
    fontSize: 13,
    fontWeight: 400,
    color: '#374151',
    fontFamily: 'Inter',
    letterSpacing: '-0.01em',
    padding: '9px 10px',
    gap: '10px',
    borderRadius: '8px',
    '&:hover': {
      backgroundColor: '#d9f7f0',
      color: '#14b8a6'
    }
  };

  const renderProfile = (compact = false) => (
    <>
      <Box
        id="accountProfile"
        onClick={handleClick}
        aria-haspopup="true"
        aria-expanded={Boolean(anchorEl)}
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          height: '38px',
          padding: '4px 8px 4px 4px',
          border: '1px solid rgba(255, 255, 255, 0.22)',
          borderRadius: '999px',
          background: 'transparent',
          cursor: 'pointer',
          transition: 'background-color 150ms ease, border-color 150ms ease',
          '&:hover': {
            backgroundColor: 'rgba(255, 255, 255, 0.08)',
            borderColor: 'rgba(255, 255, 255, 0.38)'
          }
        }}
      >
        <Box
          sx={{
            width: 28,
            height: 28,
            flexShrink: 0,
            borderRadius: '50%',
            backgroundColor: '#14b8a6',
            color: '#FFFFFF',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontWeight: 700,
            fontSize: 12,
            fontFamily: 'Inter',
            overflow: 'hidden'
          }}
        >
          {userPhoto ? (
            <img
              src={userPhoto}
              alt={fullName}
              style={{
                width: '100%',
                height: '100%',
                objectFit: 'cover'
              }}
            />
          ) : (
            initials
          )}
        </Box>
        {!compact && (
          <Typography
            sx={{
              color: '#FFFFFF',
              fontSize: 12,
              fontWeight: 500,
              fontFamily: 'Inter',
              letterSpacing: '-0.01em',
              whiteSpace: 'nowrap',
              maxWidth: 132,
              overflow: 'hidden',
              textOverflow: 'ellipsis'
            }}
          >
            {fullName}
          </Typography>
        )}
        <ChevronDown
          size={20}
          color="rgba(255,255,255,0.7)"
          style={{
            transform: anchorEl ? 'rotate(180deg)' : 'none',
            transition: 'transform 150ms ease'
          }}
        />
      </Box>
      <StyledMenu
        id="demo-customized-menu"
        anchorEl={anchorEl}
        keepMounted
        open={Boolean(anchorEl)}
        onClose={handleClose}
        PaperProps={{
          style: {
            borderRadius: 12,
            width: 236,
            minWidth: 236,
            padding: '6px',
            border: '1px solid #e5e7eb',
            boxShadow: '0 16px 40px rgba(11, 34, 57, 0.2)',
            marginTop: 8
          }
        }}
      >
        <Box
          style={{
            padding: '9px 10px',
            borderBottom: '1px solid #e5e7eb',
            marginBottom: 6
          }}
        >
          <Typography
            style={{
              fontSize: 13,
              fontWeight: 700,
              color: '#374151',
              fontFamily: 'Inter',
              letterSpacing: '-0.01em'
            }}
          >
            {fullName}
          </Typography>
          <Typography
            style={{
              fontSize: 11,
              color: '#6b7280',
              fontFamily: 'Inter',
              letterSpacing: '-0.01em',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap'
            }}
          >
            {user?.email || 'Candidate / Talent'}
          </Typography>
        </Box>
        <MenuItem
          component={RouterLink}
          to={PATH_APP.general.communities}
          onClick={handleClose}
          sx={menuItemSx}
        >
          <Users size={19} />
          <span>Communities</span>
        </MenuItem>
        <MenuItem
          component={RouterLink}
          to={PATH_APP.general.talentCard}
          onClick={handleClose}
          sx={menuItemSx}
        >
          <UserCheck size={19} />
          <span>Talent Card</span>
        </MenuItem>
        <Divider style={{ margin: '6px 4px', background: '#e5e7eb' }} />
        <MenuItem
          onClick={handleLogout}
          sx={menuItemSx}
        >
          <LogOut size={19} />
          <span>Log out</span>
        </MenuItem>
      </StyledMenu>
    </>
  );

  const renderDrawer = (
    <Drawer
      anchor="left"
      open={drawerOpen}
      onClose={toggleDrawer(false)}
      classes={{ paper: classes.drawerPaper }}
    >
      <Box className={classes.drawerNav}>
        {MOBILE_DRAWER_ITEMS.map((item) => {
          const Icon = item.icon;
          const isActive = pathname === PATH_APP.general.opportunities
            ? currentTab === item.tab
            : false;
          return (
            <button
              key={item.label}
              type="button"
              className={`${classes.drawerItem} ${
                isActive ? classes.drawerItemActive : ''
              }`}
              onClick={() => {
                setDrawerOpen(false);
                history.push(
                  `${PATH_APP.general.opportunities}?tab=${item.tab}`
                );
              }}
            >
              <Icon
                size={20}
                strokeWidth={2}
                color={isActive ? '#38CFAB' : '#64748B'}
              />
              <span>{item.label}</span>
            </button>
          );
        })}
      </Box>
    </Drawer>
  );

  return (
    <React.Fragment>
      <AppBar position={position} color="primary" className={`${classes.desktopBar} ${classes.AppBar}`}>
          <Toolbar disableGutters className={classes.mobileToolbar}>
            <Box className={classes.toolbarContainer}>
              <RouterLink
                to={brandTo}
                style={{
                  display: 'flex',
                  textDecoration: 'none',
                  alignItems: 'center',
                  gap: '10px'
                }}
              >
                <img
                  src={qrata}
                  alt="logo"
                  style={{ height: 27, width: 'auto' }}
                />
                <div
                  style={{
                    width: '1px',
                    background: 'rgba(255, 255, 255, 0.25)',
                    height: '20px'
                  }}
                ></div>
                <Typography
                  style={{
                    color: 'rgba(255, 255, 255, 0.9)',
                    fontFamily: 'Inter'
                  }}
                >
                  <span
                    style={{
                      fontSize: '13px',
                      fontWeight: 500,
                      letterSpacing: '-0.01em'
                    }}
                  >
                    For Talent
                  </span>
                </Typography>
              </RouterLink>
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center'
                }}
              >
                {!logoOnly && renderProfile(false)}
              </Box>
            </Box>
          </Toolbar>
        </AppBar>
      <AppBar position={position} className={`${classes.mobileBar} ${classes.mobileHeader}`}>
          <Toolbar className={classes.mobileToolbar}>
            <Box
              style={{ display: 'flex', alignItems: 'center', gap: '6px' }}
            >
              {!logoOnly && (
                <IconButton
                  edge="start"
                  aria-label="menu"
                  aria-haspopup="true"
                  onClick={toggleDrawer(true)}
                  className={classes.menuButton}
                >
                  <MenuIcon />
                </IconButton>
              )}
              <RouterLink
                to={brandTo}
                style={{
                  display: 'flex',
                  textDecoration: 'none',
                  color: 'white'
                }}
              >
                <img
                  src={qrata}
                  alt="logo"
                  style={{ height: 24, width: 'auto' }}
                />
              </RouterLink>
            </Box>
            {!logoOnly && renderProfile(true)}
          </Toolbar>
        </AppBar>
      {!logoOnly && renderDrawer}
    </React.Fragment>
  );
}

export default TopBar;
